<template>
  <div>
    <ul class="collection with-header">
      <li class="collection-header">
        <router-link to="/connect" class="secondary-content">Add another account</router-link>
        <h5>Connected accounts</h5>
      </li>
      <li class="collection-item avatar" v-for="account in accounts" :key="account.id">
        <img v-if="account._source.getAvatar(account)" :src="account._source.getAvatar(account)" alt="" class="circle">
        <span class="title">{{ account._source.getDisplayName(account) }}</span>
        <p>{{ account._source.label }}</p>
        <div v-if="isLoadingSources && results[account.id] && results[account.id].results.isLoading">
          <div class="progress">
            <div v-if="results[account.id].results.progress === 'indeterminate'" class="indeterminate"></div>
            <div v-else class="determinate" :style="{width: `${results[account.id].results.progress}%`}"></div>
          </div>
          {{ results[account.id].results.status }}
        </div>
        <div class="secondary-content material-icons">
          <a
            title="Disconnect this account…"
            @click="accountToDisconnect = account"
            href="#disconnect-modal"
            class="modal-trigger red-text text-darken-1"><i class="material-icons">delete_forever</i></a>
          <a title="Refresh data for this account only" @click="fetch(account.id)" class="teal-text text-darken-1"><i class="material-icons">refresh</i></a><br>
        </div>
      </li>
    </ul>
    <div id="disconnect-modal" class="modal">
      <div v-if="accountToDisconnect" class="modal-content">
        <h4>Do you want to disconnect the account {{ accountToDisconnect._source.getDisplayName(accountToDisconnect)  }}?</h4>
        <p>This will remove any associated credential, suggestions and data from your browser cache. You can always reconnect to this account afterwards by doing the "Connect" procedure again.</p>
      </div>
      <div class="modal-footer">
        <a href="#!" @click="accountToDisconnect = null" class="modal-close waves-effect waves-green btn-flat">Cancel</a>
        <a @click="$store.commit('disconnectAccount', accountToDisconnect)"  href="#!" class="modal-close waves-effect waves-green btn-flat">Confirm</a>
      </div>
    </div>
    <h2>
      Suggestions
      <button
        @click="fetch()"
        :class="['waves-effect', 'waves-light', {disabled: isLoading}, 'btn-small']" :disabled="isLoading">
        <i class="material-icons left">refresh</i>Fetch {{ maxDays }} days of data
      </button>
    </h2>
    <div class="row">
      <div class="input-field col l4">
        <input v-model="maxDays" id="maxDays" placeholder="60" type="number" min="1" step="1" max="365" class="validate">
        <label class="active" for="maxDays">Period (in days)</label>
      </div>
      <div class="input-field col l4">
        <select multiple v-model="filters.providers">
          <option v-for="provider in providers" :key="provider.id" :value="provider.id">{{ provider.label }}</option>
        </select>
        <label>Donation services</label>
      </div>
      <div class="input-field col l4">
        <select multiple v-model="filters.accounts">
          <option
            v-for="account in accounts"
            :key="account.id"
            :value="account.id">{{ account._source.getDisplayName(account) }}</option>
        </select>
        <label>Accounts</label>
      </div>
    </div>
    <div v-if="isLoading" class="progress">
      <div class="indeterminate"></div>
    </div>
    <p v-if="filters.retributeOnly">
      {{ filteredSuggestions.length }} profiles found with retribute information (<a @click.prevent="filters.retributeOnly = null">show all {{ allSuggestions.length }} profiles</a>)
    </p>
    <p v-else>
      {{ filteredSuggestions.length }} profiles found (<a @click.prevent="filters.retributeOnly = true">restrict to profiles with retribute information</a>)
    </p>
    <ul class="collection">
      <li class="collection-item avatar" v-for="suggestion in filteredSuggestions" :key="suggestion.fullId">
        <img v-if="suggestion.avatar" :src="suggestion.avatar" alt="" class="circle">
        <img v-else-if="sources[suggestion.source].getSuggestionAvatar" class="circle" :src="sources[suggestion.source].getSuggestionAvatar()">
        <i v-else-if="sources[suggestion.source].defaultAvatarIcon" class="material-icons purple accent-1 circle">{{ sources[suggestion.source].defaultAvatarIcon }}</i>
        <a target="_blank" rel="noopener noreferrer" :href="suggestion.url" class="title">{{ suggestion.name }}</a>
        <br>
        <span class="tooltipped" v-tooltip :data-tooltip="getScoreTooltip(suggestion)">
          Weight: {{ suggestion.weight }}
          <i class="tiny material-icons">help</i>
        </span>
        <a v-if="retributeProfiles[suggestion.fullId] === undefined" @click="lookup(suggestion.fullId)" class="secondary-content"><i class="material-icons">search</i></a>
        <div v-else-if="retributeProfiles[suggestion.fullId]">
          <h6>Support on:</h6>
          <template v-for="mean in retributeProfiles[suggestion.fullId].means">
            <a
              :href="mean.url"
              :key="mean.provider + mean.id"
              target="_blank"
              rel="noopener noreferrer"
              :class="['waves-effect', 'waves-light', 'btn-small']">
              <span :title="mean.summary">
                <template v-if="mean.provider === 'custom'">
                  {{ mean.summary }}
                </template>
                <template v-else>
                  {{ mean.provider }}
                </template>
              </span>
            </a>&nbsp;

          </template>
        </div>
        <div v-else>No retribute information found for this account</div>
      </li>
    </ul>
  </div>

</template>

<script>
import sources from '@/sources'
import config from '@/config'
import orderBy from 'lodash/orderBy'
import defaults from 'lodash/defaults'
import pull from 'lodash/pull'
import merge from 'lodash/merge'
import chunk from 'lodash/chunk'
import axios from 'axios'

import { mapGetters } from 'vuex'

export default {
  data () {
    let filters = this.$store.state.cache.suggestionFilters || {}
    filters = defaults(filters, {providers: [], retributeOnly: true, accounts: []})
    return {
      sources: sources.sources,
      maxDays: this.$store.state.cache.maxDays || 60,
      isLoadingSources: false,
      isLoadingRetribute: false,
      results: this.$store.state.cache.results || {},
      retributeProfiles: this.$store.state.cache.retributeProfiles || {},
      loadingRetributeProfiles: [],
      providers: this.$store.state.cache.providers || {},
      filters: filters,
      accountToDisconnect: null,
    }
  },
  mounted () {
    let elems = document.querySelectorAll('.modal')
    M.Modal.init(elems, {})
  },
  computed: {
    ...mapGetters([
      'sortedAccounts',
    ]),
    accounts () {
      return this.sortedAccounts.map((a) => {
        a._source = sources.sources[a.source]
        return a
      })
    },
    allSuggestions () {
      return Object.values(this.aggregatedSuggestions)
    },
    filteredSuggestions () {
      let self = this
      let suggestions = this.allSuggestions.filter((s) => {
        let f = true
        if (self.filters.retributeOnly != null) {
          f = f && !!this.retributeProfiles[s.fullId] === self.filters.retributeOnly
        }
        if (self.filters.providers.length > 0) {
          let p = this.retributeProfiles[s.fullId]
          f = f && p && p.means.filter((m) => {
            return self.filters.providers.indexOf(m.provider) > -1
          }).length > 0
        }
        let accountFilters = self.filters.accounts.filter((a) => {
            return !!a
          })
        if (accountFilters.length > 0) {

          f = f && s.accounts && accountFilters.filter((a) => {
            return s.accounts.indexOf(a) > -1
          }).length > 0
        }
        return f
      })
      return orderBy(suggestions, ['weight', 'id'], ['desc', 'asc'])
    },
    missingRetributeProfiles () {
      return this.allSuggestions.filter((s) => {
        return this.retributeProfiles[s.fullId] === undefined
      })
    },
    isLoading () {
      return this.isLoadingSources || this.isLoadingRetribute
    },
    aggregatedSuggestions () {
      const aggregated = {}
      this.accounts.forEach((accountObj) => {
        let account = accountObj.id
        let r = this.results[account]
        if (!r || !r.results || !r.results.accounts) {
          return
        } else {
          Object.keys(r.results.accounts).forEach((key) => {
            if (aggregated[key]) {
              aggregated[key].weight += r.results.accounts[key].weight
              merge(aggregated[key].detail,r.results.accounts[key].detail)
              aggregated[key].accounts.push(account)
            } else {
              aggregated[key] = {
                ...r.results.accounts[key], accounts: [account], fullId: key}
            }
          })
        }
      })
      return aggregated
    },
  },
  methods: {
    async fetch (id) {
      let accounts
      let self = this
      if (id) {
        accounts = this.accounts.filter((a) => {
          return a.id == id
        })
      } else {
        accounts = this.accounts
        this.results = {}
        this.retributeProfiles = {}
        this.providers = null
        this.loadingRetributeProfiles = []
      }
      this.isLoadingSources = true
      await this.fetchProviders()
      accounts.forEach((a) => {
        let r = {isLoading: true, progress: 'indeterminate', status: ''}
        let promise = a._source.fetch({account: a, store: this.$store, results: r, vue: self, maxDays: self.maxDays})
        self.$set(self.results, a.id, {account: a, promise, results: r})
      })
      const keys = Object.keys(this.results)
      for(let i = 0; i < keys.length; i++){
        await this.results[keys[i]].promise
      }
      this.$forceUpdate()
      this.isLoadingSources = false
    },
    async fetchProviders () {
      const client = axios.create()
      let url = config.RetributeAPIUrl + `v1/providers`
      const response = await axios.get(url)
      this.providers = response.data
    },
    async lookupAll () {
      let self = this
      const toLoad = this.missingRetributeProfiles.filter((p) => {
        return self.loadingRetributeProfiles.indexOf(p.fullId) === -1
      })
      if (toLoad.length === 0) {
        return
      }
      this.isLoadingRetribute = true
      const chunkSize = 10
      // lock
      toLoad.forEach((p) => {
        self.loadingRetributeProfiles.push(p.fullId)
      })
      const chunks = chunk(toLoad, chunkSize)
      for (let i = 0; i < chunks.length; i++){
        let chunk = chunks[i]
        let ids = chunk.map((s) => {
          return s.fullId
        })
        await this.lookups(ids)
      }
      this.isLoadingRetribute = false
    },
    async lookups(ids) {
      let self = this
      ids.forEach((id) => {
        self.loadingRetributeProfiles.push(id)
      })
      const client = axios.create()
      let url = config.RetributeAPIUrl + `v1/search/`
      let response
      try {
        response = await client.post(url, {lookups: ids})
      } catch {
        ids.forEach((id) => {
          self.$set(self.retributeProfiles, id, null)
        })
        return
      }
      ids.forEach((id) => {
        self.$set(self.retributeProfiles, id, response.data[id])
      })

    },
    async lookup (id) {
      this.loadingRetributeProfiles.push(id)
      const client = axios.create()
      let url = config.RetributeAPIUrl + `v1/search/${id}`
      try {
        const response = await client.get(url)
        this.$set(this.retributeProfiles, id, response.data)
      } catch {
        this.$set(this.retributeProfiles, id, null)
      }
      pull(this.loadingRetributeProfiles, [id])
    },
    getScoreTooltip (suggestion) {
      let detailPerSource = Object.entries(suggestion.detail || {}).map((e) => {
        let s = sources.sources[e[0]]
        if (!e[1]) {
          return
        }
        return s.getDetailMessage(e[1])
      }).filter((d) => {
        return !!d
      }).map((d) => {
        return `- ${d}`
      })
      let detail = detailPerSource.join('<br>')
      return `Weight is computed based on your interactions with this creator's content:<br>${detail}`
    }
  },
  watch: {
    async missingRetributeProfiles (v) {
      await this.lookupAll()
    },
    results: {
      handler (v) {
        this.$store.commit('setRecursiveState', {key: 'cache.results', value: this.results})
      },
      deep: true,
    },
    retributeProfiles: {
      handler (v) {
        this.$store.commit('setRecursiveState', {key: 'cache.retributeProfiles', value: this.retributeProfiles})
      },
      deep: true
    },
    "filters": {
      handler (v) {
        this.$store.commit('setRecursiveState', {key: 'cache.suggestionFilters', value: this.filters})
      },
      deep: true
    },
    "maxDays": {
      handler (v) {
        this.$store.commit('setRecursiveState', {key: 'cache.maxDays', value: this.maxDays})
      },
      deep: true
    },
    providers: {
      handler (v) {
        this.$store.commit('setRecursiveState', {key: 'cache.providers', value: this.providers})
        this.$nextTick(()=> {
          let elems = document.querySelectorAll('select')
          M.FormSelect.init(elems, {})
        })
      },
      immediate: true
    },
    sortedAccounts: {
      handler (v) {
        let elems = document.querySelectorAll('.modal')
        M.Modal.init(elems, {})
        // cf https://github.com/Dogfalo/materialize/issues/4622#issuecomment-298072452
        document.body.style.removeProperty("overflow");
      },
      immediate: true
    }
  }
}
</script>
<style scoped>
.avatar img.circle,
.avatar i.circle {
  border-radius: 10%;
}
</style>
